import { useState, useEffect } from "react";
import { TrendingUp, Droplets, Flame, Footprints, Moon, Brain, Dumbbell, Calendar as CalIcon, Sparkles, Loader2, Target, Zap, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";
import { Navbar } from "../components/Navbar";
import { ProtectedRoute } from "../components/ProtectedRoute";
import api from "../lib/api";

const MOOD_LABELS = ["", "Awful", "Bad", "Poor", "Low", "Okay", "Fair", "Good", "Great", "Amazing", "Perfect"];

const SimpleBarChart = ({ data, dataKey, color, maxVal }) => {
  const max = maxVal || Math.max(...data.map(d => d[dataKey] || 0), 1);
  return (
    <div className="flex items-end gap-1 h-40">
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const h = (val / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[9px] text-slate-400">{val}</span>
            <div className="w-full rounded-t-md transition-all" style={{ height: `${Math.max(h, 2)}%`, backgroundColor: color, minHeight: "2px" }} />
            <span className="text-[9px] text-slate-400 truncate w-full text-center">{d.date}</span>
          </div>
        );
      })}
    </div>
  );
};

const ProgressContent = () => {
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState({
    weight: "", calories_consumed: "", calories_burned: "", water_liters: "",
    workout_minutes: "", steps: "", sleep_hours: "", mood: "", notes: ""
  });
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [histRes, sumRes] = await Promise.all([
        api.get("/goals/history?days=30").catch(() => ({ data: [] })),
        api.get("/goals/summary").catch(() => ({ data: null })),
      ]);
      setHistory(histRes.data || []);
      setSummary(sumRes.data);
      if (sumRes.data?.today) {
        const t = sumRes.data.today;
        setForm({
          weight: t.weight || "", calories_consumed: t.calories_consumed || "",
          calories_burned: t.calories_burned || "", water_liters: t.water_liters || "",
          workout_minutes: t.workout_minutes || "", steps: t.steps || "",
          sleep_hours: t.sleep_hours || "", mood: t.mood || "", notes: t.notes || ""
        });
      }
    } catch (err) { console.error('Error:', err.message || err); }
  };

  const handleSave = async () => {
    setSaving(true);
    const dateStr = selectedDate.toISOString().split("T")[0];
    const payload = { date: dateStr };
    if (form.weight) payload.weight = parseFloat(form.weight);
    if (form.calories_consumed) payload.calories_consumed = parseInt(form.calories_consumed);
    if (form.calories_burned) payload.calories_burned = parseInt(form.calories_burned);
    if (form.water_liters) payload.water_liters = parseFloat(form.water_liters);
    if (form.workout_minutes) payload.workout_minutes = parseInt(form.workout_minutes);
    if (form.steps) payload.steps = parseInt(form.steps);
    if (form.sleep_hours) payload.sleep_hours = parseFloat(form.sleep_hours);
    if (form.mood) payload.mood = parseInt(form.mood);
    if (form.notes) payload.notes = form.notes;
    try {
      await api.post("/goals/log", payload);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const chartData = history.map(h => ({
    date: h.date?.slice(5) || "",
    calories: h.calories_consumed || 0,
    burned: h.calories_burned || 0,
    water: h.water_liters || 0,
    weight: h.weight || 0,
    workout: h.workout_minutes || 0,
    steps: h.steps || 0,
    sleep: h.sleep_hours || 0,
    mood: h.mood || 0,
  }));

  const moodNumbers = [1,2,3,4,5,6,7,8,9,10];

  const logFields = [
    { key: "weight", label: "Weight (kg)", type: "number", step: "0.1" },
    { key: "calories_consumed", label: "Calories In", type: "number" },
    { key: "calories_burned", label: "Calories Burned", type: "number" },
    { key: "water_liters", label: "Water (L)", type: "number", step: "0.1" },
    { key: "workout_minutes", label: "Workout (min)", type: "number" },
    { key: "steps", label: "Steps", type: "number" },
    { key: "sleep_hours", label: "Sleep (hrs)", type: "number", step: "0.5" },
  ];

  const summaryCards = summary ? [
    { icon: Flame, label: "Avg Calories", value: summary.weekly_avg_calories, unit: "cal", color: "text-orange-500", bg: "bg-orange-50" },
    { icon: Droplets, label: "Avg Water", value: summary.weekly_avg_water, unit: "L", color: "text-blue-500", bg: "bg-blue-50" },
    { icon: Dumbbell, label: "Avg Workout", value: summary.weekly_avg_workout, unit: "min", color: "text-green-500", bg: "bg-green-50" },
    { icon: Footprints, label: "Total Steps", value: (summary.weekly_total_steps || 0).toLocaleString(), unit: "", color: "text-purple-500", bg: "bg-purple-50" },
    { icon: CalIcon, label: "Days Logged", value: summary.logs_this_week, unit: "/7", color: "text-[#0B6E4F]", bg: "bg-[#F2F9F6]" },
    { icon: Brain, label: "Badges", value: summary.badges_earned, unit: "", color: "text-amber-500", bg: "bg-amber-50" },
  ] : [];

  return (
    <div className="min-h-screen bg-transparent" data-testid="progress-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-[#1B3A5C] p-2.5 rounded-xl">
            <TrendingUp className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#0F172A]">Goal Progress</h1>
            <p className="text-slate-500">Track your daily health metrics</p>
          </div>
        </div>

        {/* Weekly Summary Cards */}
        {summaryCards.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {summaryCards.map((s, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm" data-testid={`summary-card-${i}`}>
                <div className={`${s.bg} p-2 rounded-lg w-fit mb-2`}>
                  <s.icon className={`h-4 w-4 ${s.color}`} />
                </div>
                <p className="font-['Outfit'] text-xl font-bold text-[#0F172A]">
                  {s.value}<span className="text-xs text-slate-400 ml-0.5">{s.unit}</span>
                </p>
                <p className="text-xs text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Log Entry Form */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm sticky top-24">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A] mb-4">Log Today</h3>
              <div className="mb-4">
                <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} className="rounded-xl border" />
              </div>
              <div className="space-y-3">
                {logFields.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs font-medium text-slate-500">{f.label}</Label>
                    <Input
                      type={f.type}
                      step={f.step}
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="mt-0.5 h-9 rounded-lg bg-slate-50 text-sm"
                      data-testid={`log-${f.key}`}
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-xs font-medium text-slate-500">Mood (1-10)</Label>
                  <div className="flex gap-1 mt-1">
                    {moodNumbers.map(n => (
                      <button
                        key={n}
                        onClick={() => setForm({ ...form, mood: n })}
                        className={`w-7 h-7 rounded-lg text-xs font-bold transition-colors ${
                          Number(form.mood) === n
                            ? "bg-[#0B6E4F] text-white"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                        data-testid={`mood-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {form.mood > 0 && (
                    <p className="text-xs text-slate-400 mt-1">{MOOD_LABELS[form.mood] || ""}</p>
                  )}
                </div>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-log-btn"
                className="w-full mt-4 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full h-11 font-semibold"
              >
                {saving ? "Saving..." : "Save Log"}
              </Button>
            </div>
          </div>

          {/* Charts */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="calories">
              <TabsList className="bg-white rounded-xl p-1 shadow-sm border border-slate-100 mb-4 flex-wrap">
                <TabsTrigger value="calories" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium text-xs">Calories</TabsTrigger>
                <TabsTrigger value="weight" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium text-xs">Weight</TabsTrigger>
                <TabsTrigger value="activity" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium text-xs">Activity</TabsTrigger>
                <TabsTrigger value="wellness" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium text-xs">Wellness</TabsTrigger>
              </TabsList>

              <TabsContent value="calories">
                <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid="calories-chart">
                  <h4 className="font-['Outfit'] font-semibold text-[#0F172A] mb-1">Calorie Tracking</h4>
                  <p className="text-xs text-slate-400 mb-4">Consumed vs Burned</p>
                  {chartData.length > 0 ? (
                    <div className="space-y-6">
                      <div>
                        <p className="text-xs font-medium text-[#0B6E4F] mb-2">Consumed</p>
                        <SimpleBarChart data={chartData} dataKey="calories" color="#0B6E4F" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-red-500 mb-2">Burned</p>
                        <SimpleBarChart data={chartData} dataKey="burned" color="#EF4444" />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-12">No data yet. Start logging!</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="weight">
                <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid="weight-chart">
                  <h4 className="font-['Outfit'] font-semibold text-[#0F172A] mb-1">Weight Progress</h4>
                  <p className="text-xs text-slate-400 mb-4">Track your weight over time</p>
                  {chartData.filter(d => d.weight > 0).length > 0 ? (
                    <SimpleBarChart data={chartData.filter(d => d.weight > 0)} dataKey="weight" color="#1B3A5C" />
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-12">No weight data yet</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="activity">
                <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid="activity-chart">
                  <h4 className="font-['Outfit'] font-semibold text-[#0F172A] mb-1">Activity Overview</h4>
                  <p className="text-xs text-slate-400 mb-4">Workout minutes and steps</p>
                  {chartData.length > 0 ? (
                    <div className="space-y-6">
                      <div>
                        <p className="text-xs font-medium text-[#0B6E4F] mb-2">Workout (min)</p>
                        <SimpleBarChart data={chartData} dataKey="workout" color="#0B6E4F" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-[#1B3A5C] mb-2">Steps</p>
                        <SimpleBarChart data={chartData} dataKey="steps" color="#1B3A5C" />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-12">No activity data yet</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="wellness">
                <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid="wellness-chart">
                  <h4 className="font-['Outfit'] font-semibold text-[#0F172A] mb-1">Sleep, Water & Mood</h4>
                  <p className="text-xs text-slate-400 mb-4">Track your wellness metrics</p>
                  {chartData.length > 0 ? (
                    <div className="space-y-6">
                      <div>
                        <p className="text-xs font-medium text-purple-600 mb-2">Sleep (hours)</p>
                        <SimpleBarChart data={chartData} dataKey="sleep" color="#7C3AED" maxVal={12} />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-blue-600 mb-2">Water (liters)</p>
                        <SimpleBarChart data={chartData} dataKey="water" color="#3B82F6" maxVal={5} />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-amber-600 mb-2">Mood (1-10)</p>
                        <SimpleBarChart data={chartData} dataKey="mood" color="#F59E0B" maxVal={10} />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-12">No wellness data yet</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Recent Logs Table */}
            {history.length > 0 && (
              <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid="logs-table">
                <h4 className="font-['Outfit'] font-semibold text-[#0F172A] mb-4">Recent Logs</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="pb-2 text-left text-slate-400 font-medium">Date</th>
                        <th className="pb-2 text-right text-slate-400 font-medium">Cal In</th>
                        <th className="pb-2 text-right text-slate-400 font-medium">Cal Out</th>
                        <th className="pb-2 text-right text-slate-400 font-medium">Water</th>
                        <th className="pb-2 text-right text-slate-400 font-medium">Workout</th>
                        <th className="pb-2 text-right text-slate-400 font-medium">Steps</th>
                        <th className="pb-2 text-right text-slate-400 font-medium">Sleep</th>
                        <th className="pb-2 text-right text-slate-400 font-medium">Mood</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice().reverse().slice(0, 7).map((log, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-2 text-slate-700 font-medium">{log.date}</td>
                          <td className="py-2 text-right text-slate-600">{log.calories_consumed || "-"}</td>
                          <td className="py-2 text-right text-slate-600">{log.calories_burned || "-"}</td>
                          <td className="py-2 text-right text-slate-600">{log.water_liters ? `${log.water_liters}L` : "-"}</td>
                          <td className="py-2 text-right text-slate-600">{log.workout_minutes ? `${log.workout_minutes}m` : "-"}</td>
                          <td className="py-2 text-right text-slate-600">{log.steps?.toLocaleString() || "-"}</td>
                          <td className="py-2 text-right text-slate-600">{log.sleep_hours ? `${log.sleep_hours}h` : "-"}</td>
                          <td className="py-2 text-right text-slate-600">{log.mood || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* AI Performance Analysis */}
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid="ai-analysis-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="bg-gradient-to-br from-[#0B6E4F] to-[#1B3A5C] p-2 rounded-xl">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-['Outfit'] font-semibold text-[#0F172A]">AI Performance Analysis</h4>
                    <p className="text-xs text-slate-400">Powered by Vita AI</p>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    setAnalysisLoading(true);
                    try {
                      const res = await api.get("/ai-analysis");
                      setAnalysis(res.data);
                    } catch { alert("Failed to generate analysis"); }
                    setAnalysisLoading(false);
                  }}
                  disabled={analysisLoading}
                  data-testid="generate-analysis-btn"
                  className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full text-xs px-4"
                >
                  {analysisLoading ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Analyzing...</> : <><Sparkles className="h-3 w-3 mr-1" /> Analyze</>}
                </Button>
              </div>

              {analysis?.message && !analysis?.analysis && (
                <p className="text-sm text-slate-500 text-center py-6">{analysis.message}</p>
              )}

              {analysis?.analysis && (
                <div className="space-y-4" data-testid="analysis-results">
                  {/* Score + Trend */}
                  <div className="flex items-center gap-4 bg-[#F2F9F6] rounded-xl p-4">
                    <div className="text-center">
                      <p className="font-['Outfit'] text-3xl font-bold text-[#0B6E4F]">{analysis.analysis.overall_score}</p>
                      <p className="text-xs text-slate-500">Score</p>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1 mb-1">
                        {analysis.analysis.trend === "improving" ? <ArrowUpRight className="h-4 w-4 text-emerald-500" /> : analysis.analysis.trend === "declining" ? <ArrowDownRight className="h-4 w-4 text-red-500" /> : <Minus className="h-4 w-4 text-slate-400" />}
                        <span className="text-sm font-medium capitalize text-[#0F172A]">{analysis.analysis.trend}</span>
                      </div>
                      <p className="text-xs text-slate-500">{analysis.analysis.weekly_summary}</p>
                    </div>
                  </div>

                  {analysis.analysis.top_achievement && (
                    <div className="flex items-center gap-2 bg-amber-50 rounded-xl p-3">
                      <Target className="h-4 w-4 text-amber-600" />
                      <p className="text-xs text-amber-800"><span className="font-semibold">Top Achievement:</span> {analysis.analysis.top_achievement}</p>
                    </div>
                  )}

                  {analysis.analysis.focus_area && (
                    <div className="flex items-center gap-2 bg-blue-50 rounded-xl p-3">
                      <Zap className="h-4 w-4 text-blue-600" />
                      <p className="text-xs text-blue-800"><span className="font-semibold">Focus Area:</span> {analysis.analysis.focus_area}</p>
                    </div>
                  )}

                  {/* Insights */}
                  <div className="space-y-2">
                    {(analysis.analysis.insights || []).map((insight, i) => {
                      const priorityColors = { high: "border-l-red-400 bg-red-50/30", medium: "border-l-amber-400 bg-amber-50/30", low: "border-l-blue-400 bg-blue-50/30" };
                      return (
                        <div key={i} className={`border-l-4 rounded-r-xl p-3 ${priorityColors[insight.priority] || "border-l-slate-300"}`} data-testid={`insight-${i}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold uppercase text-slate-500">{insight.category}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${insight.priority === "high" ? "bg-red-100 text-red-700" : insight.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{insight.priority}</span>
                          </div>
                          <p className="text-sm font-medium text-[#0F172A] mb-0.5">{insight.title}</p>
                          <p className="text-xs text-slate-600 mb-1">{insight.finding}</p>
                          <p className="text-xs text-[#0B6E4F] font-medium">{insight.recommendation}</p>
                        </div>
                      );
                    })}
                  </div>

                  {analysis.cached && (
                    <p className="text-[10px] text-slate-400 text-center">Cached analysis - regenerates every 24h</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function GoalProgress() {
  return (
    <ProtectedRoute>
      <ProgressContent />
    </ProtectedRoute>
  );
}
